import { Flex, Pane, Text } from '@fullstory/components';
import React from 'react';

const DEFAULT_COLOR = '#CEDFFF';
type Size = 'xsmall' | 'small' | 'large' | 'xlarge';

interface AvatarProps {
  name?: string;
  color?: string;
  size: Size;
  /**
   * If left undefined, no abbreviation function will be applied.
   */
  abbreviationChars?: 1 | 2;
  textColor?: React.ComponentProps<typeof Text>['color'];
}

type PaneStyleProps = {
  borderRadius: React.ComponentProps<typeof Pane>['borderRadius'];
  width: React.ComponentProps<typeof Pane>['width'];
  height: React.ComponentProps<typeof Pane>['height'];
};

const styles: { [size in Size]: PaneStyleProps } = {
  xsmall: {
    borderRadius: 2,
    width: 8,
    height: 8,
  },
  small: {
    borderRadius: 4,
    width: 16,
    height: 16,
  },
  large: {
    borderRadius: 6,
    width: 24,
    height: 24,
  },
  xlarge: {
    borderRadius: 6,
    width: 32,
    height: 32,
  },
};

export const Avatar: React.FC<AvatarProps> = ({
  name,
  color = DEFAULT_COLOR,
  size,
  abbreviationChars,
  textColor,
}) => {
  const nameText =
    !!name && !!abbreviationChars ? getAbbreviation(name, abbreviationChars) : name ?? null;

  return (
    <Pane
      data-component="Avatar"
      {...styles[size]}
      style={{ backgroundColor: color, flexShrink: '0' }}
      testId="avatar"
    >
      {!!nameText && (
        <Flex column style={{ height: '100%' }}>
          <Text appearance="bodySmall" color={textColor}>
            {nameText}
          </Text>
        </Flex>
      )}
    </Pane>
  );
};

const getAbbreviation = (name: string, chars: number) =>
  name
    .match(/\b([A-Za-z0-9])/g)
    ?.join('')
    ?.toUpperCase()
    ?.substring(0, chars);
